// UC50-11 / UC50-12 — import stock movements from AMDM's C2 workbook.
// per docs/ui-shape-stock-import.md, decision D10 in
// docs/decizii-paritate-wireframe-2026-09-05.md.
//
// The wireframe (wireframes/react/src/pages/Import.tsx) draws three steps:
// choose the file, validate it, read the result. The middle step is the reason
// the screen exists, so nothing is written until the operator has seen every
// row the file got wrong.
//
// There is NO import endpoint. The rows go through the draft the manual editor
// already uses, and core-service re-validates them there; `c2-template.ts` ends
// with what a dedicated endpoint would need.
//
// Since specs/054 (US7, research R11) the import targets a storage location,
// chosen the way the report chooses it, and EVERY file row enters that
// location's draft: a bad cell travels as a source issue and the row is marked
// there, to be corrected before the save.
import { Fragment, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudFileInput,
  MudFileItem,
  MudIcon,
  MudInfoBox,
  MudToast,
  MudStepper,
  MudSelect,
  MudSpinner,
  MudTable,
  MudTag,
} from '@egov-moldova/mud-react';
import { Breadcrumb, PageHeader } from '@simsm/ui-shared';
import { apiErrorKey } from '../../../../app/api-client';
import {
  createDraftWithRows,
  type DraftRow,
  type DraftState,
} from '../../../../features/reporting/api';
import { draftQueryKey } from '../../../../features/reporting/use-draft';
import { useReportingLocations } from '../../../../features/reporting/use-locations';
import {
  buildImportReport,
  C2_REASONS,
  reportIsImportable,
  withSelectedSite,
  type ImportError,
  type ImportReport,
} from './c2-template';
import { buildC2Template, readC2Workbook, saveBlob, TEMPLATE_FILENAME } from './workbook';

// mud-table's own column/row types are not re-exported from the package root.
interface TableColumn {
  key: string;
  label: string;
  align?: 'start' | 'center' | 'end';
  width?: string;
}
type TableRowData = Record<string, string>;

interface SelectOption {
  value: string;
  label: string;
}

interface SelectChangeDetail {
  value: string;
}

/** What the draft answered, and how many of the file's rows it marked. */
interface ImportOutcome {
  draft: DraftState;
  processed: number;
  errorRows: number;
}

const ACCEPT = '.xlsx';

// A file can be wrong on hundreds of rows; the table is a place to start, not a
// log. The count above it stays exact.
const MAX_LISTED_ERRORS = 100;

function stepStatus(index: number, current: number): 'pending' | 'current' | 'completed' {
  if (index < current) return 'completed';
  if (index === current) return 'current';
  return 'pending';
}

function allErrors(report: ImportReport): ImportError[] {
  return [...report.fileErrors, ...report.rows.flatMap((row) => row.errors)];
}

export function StockImportPage(): JSX.Element {
  const { t } = useTranslate();
  const { role = 'registrar-operator' } = useParams<{ role: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    locations,
    registrarKind,
    registrarKindState,
    isLoading: locationsLoading,
    isError: locationsFailed,
  } = useReportingLocations();
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ImportReport | null>(null);
  const [imported, setImported] = useState<ImportOutcome | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // The location the file is imported for, as on the report (research R2: the
  // previous stock is per location). One location needs no choosing.
  const [chosenSiteId, setChosenSiteId] = useState<string | null>(null);
  const siteId = chosenSiteId ?? (locations.length === 1 ? (locations[0]?.id ?? null) : null);
  const site = locations.find((location) => location.id === siteId) ?? null;

  // A file reported for another location is refused as a whole (R11), and
  // re-checked whenever the location changes.
  const report = useMemo(
    () => (parsed && site ? withSelectedSite(parsed, site.internalCode) : parsed),
    [parsed, site],
  );

  const parse = useMutation({
    mutationFn: async (selected: File) => {
      const { headers, rows } = await readC2Workbook(selected);
      return buildImportReport(headers, rows);
    },
    onSuccess: (data) => {
      setParsed(data);
      setErrorMessage(null);
    },
    onError: () => {
      setParsed(null);
      setErrorMessage(t('stock_import.error.read_failed'));
    },
  });

  // One POST carries the whole file: the bff creates the location's draft with
  // these rows, or appends them to the open one, atomically (FR-023). No read
  // first, so no version to race with the report page.
  const runImport = useMutation({
    mutationFn: async (input: { siteId: string; rows: DraftRow[]; localErrors: Set<string> }) => {
      const draft = await createDraftWithRows(input.siteId, input.rows);
      const ids = new Set(input.rows.map((row) => row.clientRowId));
      // Core re-validates every row, so the count is the draft's; a row the file
      // check already marked counts even if the answer omits its errors.
      const errorRows = draft.rows.filter(
        (row) =>
          ids.has(row.clientRowId) &&
          (row.validationErrors.length > 0 || input.localErrors.has(row.clientRowId)),
      ).length;
      return { draft, processed: input.rows.length, errorRows };
    },
    onSuccess: (outcome) => {
      setImported(outcome);
      setErrorMessage(null);
      void queryClient.invalidateQueries({ queryKey: draftQueryKey(outcome.draft.siteId || null) });
      void queryClient.invalidateQueries({ queryKey: ['drafts-count'] });
      void queryClient.invalidateQueries({ queryKey: ['drafts-register'] });
    },
    onError: (err) => setErrorMessage(t(apiErrorKey(err, 'stock_import.error.import_failed'))),
  });

  const downloadTemplate = useMutation({
    // The example row names a reason the active Registrar's type allows (CF-15). The
    // button waits for that type, so there always is one by the time it is pressed.
    mutationFn: async () => {
      if (registrarKind) saveBlob(await buildC2Template(registrarKind), TEMPLATE_FILENAME);
    },
    onError: () => setErrorMessage(t('stock_import.error.template_failed')),
  });

  const step = imported ? 2 : report ? 1 : 0;
  const busy = parse.isPending || runImport.isPending;

  function reset(): void {
    setFile(null);
    setParsed(null);
    setImported(null);
    setErrorMessage(null);
  }

  function errorLabel(error: ImportError): string {
    return t(`stock_import.error.${error.code}`, {
      column: error.column,
      value: error.value,
      limit: error.limit ?? 0,
      reasons: C2_REASONS.join(', '),
      site: site?.internalCode ?? '',
    });
  }

  function startImport(): void {
    if (!report || !siteId) return;
    runImport.mutate({
      siteId,
      rows: report.rows.map((row) => row.draftRow),
      localErrors: new Set(
        report.rows.filter((row) => row.errors.length > 0).map((row) => row.draftRow.clientRowId),
      ),
    });
  }

  const locationOptions: SelectOption[] = locations.map((location) => ({
    value: location.id,
    label: location.internalCode ? `${location.name} · ${location.internalCode}` : location.name,
  }));

  const listed = report ? allErrors(report).slice(0, MAX_LISTED_ERRORS) : [];
  const totalErrors = report ? allErrors(report).length : 0;

  const errorColumns: TableColumn[] = [
    { key: 'line', label: t('stock_import.col.line'), width: '80px' },
    { key: 'column', label: t('stock_import.col.column'), width: '220px' },
    { key: 'message', label: t('stock_import.col.message') },
  ];
  const errorRows: TableRowData[] = listed.map((error, i) => ({
    id: `${error.line}-${error.column}-${i}`,
    line: String(error.line),
    column: error.column,
    message: errorLabel(error),
  }));

  return (
    <section className="simsm-page simsm-import">
      <Breadcrumb
        label={t('common.breadcrumb.aria_label')}
        crumbs={[
          { label: t('stock_import.breadcrumb.reporting'), to: `/${role}/reporting/new` },
          { label: t('stock_import.breadcrumb.current') },
        ]}
      />

      <PageHeader
        title={t('stock_import.title')}
        lede={t('stock_import.lede')}
        meta={
          report && report.siteCode !== ''
            ? t('stock_import.meta.site', { code: report.siteCode })
            : undefined
        }
        actions={
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            // Its example row follows the Registrar's type, so not before it is known.
            disabled={downloadTemplate.isPending || registrarKindState !== 'ready'}
            onClick={() => downloadTemplate.mutate()}
          >
            <MudIcon slot="icon-start" name="download" size={16} />
            {t('stock_import.download_template')}
          </MudButton>
        }
      />

      <MudStepper
        className="simsm-import__steps"
        currentStep={step}
        aria-label={t('stock_import.steps.aria')}
        steps={[
          { label: t('stock_import.step.choose'), status: stepStatus(0, step) },
          { label: t('stock_import.step.validate'), status: stepStatus(1, step) },
          { label: t('stock_import.step.result'), status: stepStatus(2, step) },
        ]}
      />

      {errorMessage ? (
        <MudInfoBox emphasis="strong" role="alert" variant="error">
          {errorMessage}
        </MudInfoBox>
      ) : null}

      {/* The same required control as the report's toolbar; AGE draws the
          asterisk. Locked once the rows are in: they belong to that location. */}
      <div className="simsm-import__location">
        <MudSelect
          size="medium"
          required
          label={t('stock_reporting.location.label')}
          placeholder={t('stock_reporting.location.placeholder')}
          value={siteId ?? ''}
          disabled={locationsLoading || locationOptions.length === 0 || busy || imported !== null}
          invalid={!locationsLoading && siteId === null && report !== null}
          onMudChange={(e: CustomEvent<SelectChangeDetail>) =>
            setChosenSiteId(e.detail.value === '' ? null : e.detail.value)
          }
        >
          {locationOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </MudSelect>
      </div>

      {locationsFailed && locations.length === 0 ? (
        <MudInfoBox
          emphasis="strong"
          role="alert"
          variant="error"
          titleText={t('stock_reporting.location.load_error.title')}
        >
          {t('stock_reporting.location.load_error.body')}
        </MudInfoBox>
      ) : !locationsLoading && locationOptions.length === 0 ? (
        <MudInfoBox
          emphasis="strong"
          variant="warning"
          titleText={t('stock_reporting.location.none.title')}
        >
          {t('stock_reporting.location.none.body')}
        </MudInfoBox>
      ) : null}

      {/* The dropzone stands on the page, not in a card: the wireframe draws it
          bare, and `.simsm-import` already stretches its children, so no rule of
          our own is needed for it to span the column. */}
      {step === 0 && !parse.isPending ? (
        <MudFileInput
          accept={ACCEPT}
          variant="dropzone"
          size="lg"
          onMudChange={(e: CustomEvent<{ files: File[] }>) => {
            const next = e.detail.files[0] ?? null;
            setFile(next);
            setImported(null);
            if (next) parse.mutate(next);
          }}
        />
      ) : null}

      {parse.isPending ? (
        <div className="simsm-import__panel" role="status" aria-live="polite">
          <MudSpinner size="md" label={t('common.loading')} />
          <p className="simsm-import__hint">{t('stock_import.validating')}</p>
        </div>
      ) : null}

      {report && file && !parse.isPending ? (
        <>
          <MudFileItem
            removeLabel={t('common.file_input.remove')}
            filename={file.name}
            size={file.size}
            state={report.errorCount > 0 || report.fileErrors.length > 0 ? 'error' : 'success'}
            noRemove={busy}
            onMudRemove={reset}
          />

          <div className="simsm-import__tiles">
            <div className="simsm-import__tile">
              <span className="simsm-import__tile-key">{t('stock_import.tile.total')}</span>
              <span className="simsm-import__tile-value">{report.totalCount}</span>
            </div>
            <div className="simsm-import__tile">
              <span className="simsm-import__tile-key">{t('stock_import.tile.valid')}</span>
              <span className="simsm-import__tile-value">{report.validCount}</span>
            </div>
            <div className="simsm-import__tile">
              <span className="simsm-import__tile-key">{t('stock_import.tile.errors')}</span>
              <span className="simsm-import__tile-value">{report.errorCount}</span>
            </div>
          </div>

          {imported ? (
            <div className="simsm-inline-outcome">
              <MudToast closable={false} variant="success">
                {t('stock_import.imported.processed', { count: imported.processed })}
              </MudToast>
            </div>
          ) : null}

          {imported ? (
            imported.errorRows > 0 ? (
              <MudInfoBox
                variant="warning"
                emphasis="strong"
                titleText={t('stock_import.imported.with_errors', { errors: imported.errorRows })}
              >
                {t('stock_import.imported.with_errors_body')}
              </MudInfoBox>
            ) : null
          ) : report.fileErrors.length > 0 ? (
            <MudInfoBox
              variant="warning"
              emphasis="strong"
              titleText={t('stock_import.blocked.title')}
            >
              {t('stock_import.blocked.body')}
            </MudInfoBox>
          ) : report.errorCount > 0 ? (
            <MudInfoBox
              variant="warning"
              emphasis="strong"
              titleText={
                report.errorCount === 1
                  ? t('stock_import.partial.title_one')
                  : t('stock_import.partial.title', { errors: report.errorCount })
              }
            >
              {t('stock_import.partial.body')}
            </MudInfoBox>
          ) : (
            <MudInfoBox emphasis="strong" variant="info" titleText={t('stock_import.clean.title')}>
              {t('stock_import.clean.body', { valid: report.validCount })}
            </MudInfoBox>
          )}

          {listed.length > 0 ? (
            <div className="simsm-import__errors">
              <div className="simsm-import__errors-table">
                <MudTable
                  className="simsm-table"
                  columns={errorColumns}
                  rows={errorRows}
                  rowStyle="divided"
                  disableSort={true}
                  aria-label={t('stock_import.errors.aria')}
                >
                  {listed.map((error, i) => (
                    <Fragment key={`${error.line}-${error.column}-${i}`}>
                      {error.column === '' ? null : (
                        <span slot={`cell-column-${i}`}>
                          <MudTag variant="status" semantic="neutral" label={error.column} />
                        </span>
                      )}
                      {/* Slotted so the sentence wraps instead of taking
                          mud-table's one-line ellipsis. */}
                      <span slot={`cell-message-${i}`} className="simsm-import__errors-message">
                        {errorLabel(error)}
                      </span>
                    </Fragment>
                  ))}
                </MudTable>
              </div>
              {totalErrors > listed.length ? (
                <p className="simsm-import__hint">
                  {t('stock_import.errors_truncated', {
                    shown: listed.length,
                    total: totalErrors,
                  })}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="simsm-import__actions">
            <MudButton shape="circular" variant="neutral" size="md" disabled={busy} onClick={reset}>
              {t('stock_import.action.other_file')}
            </MudButton>
            {imported ? (
              <MudButton
                shape="circular"
                variant="primary"
                size="md"
                // FR-032: back to THAT draft, at its own address (T064).
                onClick={() => navigate(`/${role}/reporting/drafts/${imported.draft.id}`)}
              >
                {t('stock_import.action.continue')}
              </MudButton>
            ) : (
              <MudButton
                shape="circular"
                variant="primary"
                size="md"
                disabled={busy || siteId === null || !reportIsImportable(report)}
                onClick={startImport}
              >
                {runImport.isPending ? (
                  <MudSpinner slot="icon-start" size="sm" label={t('common.loading')} />
                ) : null}
                {t('stock_import.action.add_rows', { count: report.totalCount })}
              </MudButton>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}
